import React, { useMemo } from 'react';
import { Progress, Typography, Button, Space } from 'antd';
import { 
  LoadingOutlined, 
  ThunderboltOutlined, 
  CheckCircleOutlined,
  ReloadOutlined,
  WifiOutlined,
  DisconnectOutlined 
} from '@ant-design/icons';
import { useSSEProgress } from '../hooks/useSSEProgress';
import { SSE_ENABLED } from '../config/app';
import './ProgressDisplay.css';

const { Text } = Typography;

const ProgressDisplay = ({ 
  jobId, 
  jobData, 
  size = 'default', 
  showText = true, 
  showIcon = true, 
  showDetails = false,
  showSSEConnection = false,
  useSSE = SSE_ENABLED, // Use global config instead of defaulting to true
  className = ''
}) => {
  // Use SSE hook if jobId is provided and useSSE is enabled
  const { 
    progress: sseProgress, 
    connectionStatus, 
    error: sseError, 
    refreshProgress,
    isConnected,
    isPolling,
    isReconnecting,
    hasError 
  } = useSSEProgress(jobId && useSSE ? jobId : null);

  // Use SSE data if available, otherwise fall back to provided jobData
  const currentProgress = sseProgress || jobData;

  // Calculate progress metrics
  const progressMetrics = useMemo(() => {
    if (!currentProgress) return null;
    
    const processed = currentProgress.processed_frames || currentProgress.processed || 0;
    const total = currentProgress.total_frames || currentProgress.total || 1;
    const percentage = Math.round((processed / total) * 100);
    const remaining = Math.max(0, total - processed);
    
    // Estimate time remaining based on progress rate
    const startedAt = currentProgress.started_at || currentProgress.created_at;
    const timeElapsed = startedAt ? Date.now() - (startedAt * 1000 || Date.now()) : 0;
    const processingRate = timeElapsed > 0 ? processed / (timeElapsed / 1000) : 0; // frames per second
    const estimatedTimeRemaining = processingRate > 0 ? remaining / processingRate : 0; // seconds
    
    return {
      processed,
      total,
      percentage,
      remaining,
      estimatedTimeRemaining: Math.round(estimatedTimeRemaining),
      processingRate: Math.round(processingRate * 10) / 10
    };
  }, [currentProgress]);

  const formatProgressDisplay = (progressData) => {
    const { 
      processed_frames, 
      total_frames, 
      progress, 
      progress_phase, 
      status,
      extraction_status,
      processed,
      total
    } = progressData;
    
    // Normalize frame counts (SSE vs legacy format)
    const frames_processed = processed_frames || processed || 0;
    const frames_total = total_frames || total || 1;
    const progress_percent = progress || ((frames_processed / frames_total) * 100);
    
    // Handle completed jobs
    if (status === 'completed') {
      return {
        text: `Processing: ${frames_processed}/${frames_total} frames (100.0%)`,
        indicator: '',
        phase: 'completed',
        statusText: 'Complete! Ready for export.',
        showSpinner: false,
        colorClass: 'progress-completed',
        strokeColor: {
          '0%': '#52c41a',
          '100%': '#73d13d',
        }
      };
    }
    
    // Handle error states
    if (status === 'error') {
      return {
        text: `Processing failed: ${frames_processed}/${frames_total} frames`,
        indicator: '[error]',
        phase: 'error',
        statusText: 'Processing failed. Please check error details.',
        showSpinner: false,
        colorClass: 'progress-error',
        strokeColor: '#ff4d4f'
      };
    }
    
    // Handle extraction phase
    if (progress_phase === 'extracting' || extraction_status !== 'completed') {
      return {
        text: `Processing: ${frames_processed}/~${frames_total} frames (${progress_percent?.toFixed(1) || 0}%)`,
        indicator: '[extracting]',
        phase: 'extracting',
        statusText: 'Extracting frames from video...',
        showSpinner: true,
        colorClass: 'progress-extracting',
        strokeColor: {
          '0%': '#ff9800',
          '100%': '#ffc107',
        }
      };
    }
    
    // Handle analysis phase
    return {
      text: `Processing: ${frames_processed}/${frames_total} frames (${progress_percent?.toFixed(1) || 0}%)`,
      indicator: '',
      phase: 'analyzing',
      statusText: 'Analyzing video content...',
      showSpinner: true,
      colorClass: 'progress-analyzing',
      strokeColor: {
        '0%': '#2196f3',
        '100%': '#03a9f4',
      }
    };
  };

  // Return early with loading state if no data available
  if (!currentProgress && !hasError) {
    return (
      <div className={`progress-display loading ${className}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '20px' }}>
          <LoadingOutlined />
          <Text type="secondary">Loading progress...</Text>
        </div>
      </div>
    );
  }

  // Return error state if there's an error and no progress data
  if (hasError && !currentProgress) {
    return (
      <div className={`progress-display error ${className}`}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          padding: '16px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '6px'
        }}>
          <span>⚠️ Unable to load progress: {sseError}</span>
          <Button size="small" onClick={refreshProgress}>Retry</Button>
        </div>
      </div>
    );
  }

  const progressData = formatProgressDisplay(currentProgress);
  
  // Get the appropriate icon based on phase
  const getPhaseIcon = (phase, showSpinner) => {
    if (!showIcon) return null;
    
    switch (phase) {
      case 'extracting':
        return showSpinner ? <LoadingOutlined spin /> : <LoadingOutlined />;
      case 'analyzing':
        return showSpinner ? <ThunderboltOutlined /> : <ThunderboltOutlined />;
      case 'completed':
        return <CheckCircleOutlined />;
      case 'error':
        return null; // Progress component will show error styling
      default:
        return showSpinner ? <LoadingOutlined spin /> : <LoadingOutlined />;
    }
  };

  // Connection status indicator
  const ConnectionStatus = () => {
    if (!showSSEConnection || !useSSE) return null;

    const getStatusConfig = () => {
      switch (connectionStatus) {
        case 'connected':
          return { color: '#28a745', icon: <WifiOutlined />, text: 'Real-time', bgColor: '#e8f5e8' };
        case 'polling':
          return { color: '#ffc107', icon: <ReloadOutlined />, text: 'Polling', bgColor: '#fff3cd' };
        case 'reconnecting':
          return { color: '#fd7e14', icon: <LoadingOutlined spin />, text: 'Reconnecting...', bgColor: '#fff3cd' };
        case 'error':
          return { color: '#dc3545', icon: <DisconnectOutlined />, text: 'Connection Error', bgColor: '#f8d7da' };
        case 'completed':
          return { color: '#28a745', icon: <CheckCircleOutlined />, text: 'Completed', bgColor: '#e8f5e8' };
        default:
          return { color: '#6c757d', icon: <LoadingOutlined />, text: 'Connecting...', bgColor: '#f8f9fa' };
      }
    };

    const statusConfig = getStatusConfig();

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 12px',
        borderRadius: '16px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: statusConfig.bgColor,
        color: statusConfig.color
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: statusConfig.color,
          animation: connectionStatus === 'connected' ? 'pulse 2s infinite' : 'none'
        }} />
        <span>{statusConfig.text}</span>
        {hasError && (
          <Button 
            size="small"
            type="text"
            onClick={refreshProgress}
            style={{ color: statusConfig.color, padding: '2px 4px' }}
          >
            ↻
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className={`progress-display ${progressData.colorClass} ${className}`}>
      {/* Header with connection status */}
      {showSSEConnection && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <Text strong>Processing Progress</Text>
          <ConnectionStatus />
        </div>
      )}

      {/* Main progress bar */}
      <Progress
        percent={Math.round(progressMetrics?.percentage || currentProgress?.progress || 0)}
        size={size}
        status={currentProgress?.status === 'error' ? 'exception' : 
                progressData.phase === 'completed' ? 'success' : 'active'}
        strokeColor={progressData.strokeColor}
        format={(percent) => showIcon ? (
          <span className="progress-content">
            {getPhaseIcon(progressData.phase, progressData.showSpinner)}
            <span style={{ marginLeft: '4px' }}>{percent}%</span>
          </span>
        ) : `${percent}%`}
      />
      
      {/* Progress text and status */}
      {showText && (
        <div className="progress-text-container">
          <Text className="progress-text">
            {progressData.text}
            {progressData.indicator && (
              <span className="phase-indicator">{progressData.indicator}</span>
            )}
          </Text>
          <Text type="secondary" className="status-text">
            {progressData.statusText}
          </Text>
          {currentProgress?.errors > 0 && (
            <Text type="danger" style={{ fontSize: '12px' }}>
              ⚠️ {currentProgress.errors} errors
            </Text>
          )}
        </div>
      )}

      {/* Detailed metrics */}
      {showDetails && progressMetrics && (
        <div style={{ 
          borderTop: '1px solid #e9ecef', 
          paddingTop: '16px', 
          marginTop: '16px' 
        }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">Processing Rate:</Text>
              <Text strong>{progressMetrics.processingRate} frames/sec</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">Estimated Time Remaining:</Text>
              <Text strong>
                {progressMetrics.estimatedTimeRemaining < 60 
                  ? `${progressMetrics.estimatedTimeRemaining}s`
                  : `${Math.round(progressMetrics.estimatedTimeRemaining / 60)}m`
                }
              </Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">Job Status:</Text>
              <Text strong style={{ 
                color: currentProgress?.status === 'completed' ? '#28a745' :
                       currentProgress?.status === 'error' ? '#dc3545' : '#007bff'
              }}>
                {currentProgress?.status || 'processing'}
              </Text>
            </div>
            {currentProgress?.last_update && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Last Updated:</Text>
                <Text strong>
                  {new Date(currentProgress.last_update * 1000).toLocaleTimeString()}
                </Text>
              </div>
            )}
          </Space>
        </div>
      )}
      
      {/* Real-time indicator */}
      {isConnected && showSSEConnection && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '12px',
          padding: '8px 12px',
          backgroundColor: '#e8f5e8',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#2d5a2d'
        }}>
          <div style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: '#28a745',
            animation: 'pulse 1.5s infinite'
          }} />
          <span>Live updates</span>
        </div>
      )}
    </div>
  );
};

export default ProgressDisplay;
